import Typography from '@mui/material/Typography';
import { DatePickerInput } from 'customer_portal-ui-shared';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../hooks';
import { Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface DateRangeProps {
    defaultBefore: Date | null; // Change type to Date or null
    defaultAfter: Date | null; // Change type to Date or null
    onChangeBefore?: (value: Date | null) => void; // Change type to Date or null
    onChangeAfter?: (value: Date | null) => void; // Change type to Date or null
    tooltipMessage?: string; // Optional tooltip message
    hideBeforeLabel?: boolean; // Hide the "On or Before Date" label
}

const DateRanges = ({ defaultBefore, defaultAfter, onChangeBefore, onChangeAfter, tooltipMessage, hideBeforeLabel = false }: DateRangeProps) => {
    const [BeforeDate, setBeforeDate] = useState<Date | null>(defaultBefore); // Set initial state to DefaultBefore
    const [AfterDate, setAfterDate] = useState<Date | null>(defaultAfter); // Set initial state to DefaultAfter
    const isFirstBeforeRender = useRef(true);
    const isFirstAfterRender = useRef(true);

    // Skip mount so expanding a collapsed filter does not dispatch SetCriteria / refetch
    useEffect(() => {
        if (isFirstBeforeRender.current) {
            isFirstBeforeRender.current = false;
            return;
        }
        onChangeBefore?.(BeforeDate);
    }, [BeforeDate]);

    useEffect(() => {
        if (isFirstAfterRender.current) {
            isFirstAfterRender.current = false;
            return;
        }
        onChangeAfter?.(AfterDate);
    }, [AfterDate]);

    const handleKeyDownBeforeDate = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Delete' || event.key === 'Backspace') {
            setBeforeDate(null);
        }
    };

    const handleKeyDownAfterDate = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Delete' || event.key === 'Backspace') {
            setAfterDate(null);
        }
    };

    const showAfterDate = onChangeAfter !== null && onChangeAfter !== undefined;
    const showBeforeDate = onChangeBefore !== null && onChangeBefore !== undefined;

    return (
        <div>
            {showAfterDate && (
                <>
                    <DatePickerInput onChange={setAfterDate} value={defaultAfter} label="On or After Date" withBorder onKeyDown={handleKeyDownAfterDate} />
                    {showBeforeDate && (
                        <Typography
                            variant="overline"
                            component="div"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                margin: '0 12px',
                                fontSize: 12,
                                mt: '5px'
                            }}
                        >
                            {'To'}
                        </Typography>
                    )}
                </>
            )}
            {showBeforeDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                        <DatePickerInput 
                            onChange={setBeforeDate} 
                            value={defaultBefore} 
                            label={hideBeforeLabel ? '' : 'On or Before Date'} 
                            withBorder 
                            onKeyDown={handleKeyDownBeforeDate} 
                        />
                    </div>
                    {tooltipMessage && (
                        <Tooltip title={tooltipMessage} arrow placement="right">
                            <IconButton size="small" sx={{ mt: hideBeforeLabel ? 0 : 1 }}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};

export default DateRanges;
